import type cytoscape from 'cytoscape'

import * as operations from './operations'
import * as visibility from './visibility'

const DEFAULT_VISIBLE_DEPTH = 1
const INITIAL_ZOOM = 1.35

/**
 * Collapses/expands tree levels on click and hides deeper levels by default.
 */
export function setupExpandCollapse(
  cy: cytoscape.Core,
  {
    defaultVisibleDepth = DEFAULT_VISIBLE_DEPTH,
  }: setupExpandCollapse.AdditionalParams = {},
): void {
  const depths = visibility.computeDepths(cy)

  applyVisibilityByDepth(cy, depths, defaultVisibleDepth)
  visibility.refreshExpandCollapseState(cy)
  operations.runLayoutPreservingViewport(cy, {
    onLayoutStop: () => {
      const visibleNodes = cy.nodes().filter(node => visibility.isVisible(node))
      if (visibleNodes.length === 0) {
        return
      }
      cy.zoom(INITIAL_ZOOM)
      cy.center(visibleNodes)
    },
  })

  // Use mousedown to intercept badge clicks BEFORE cytoscape processes them
  // This prevents node selection when clicking on the badge
  const container = cy.container()
  if (container) {
    container.addEventListener(
      'mousedown',
      (mouseEvent: MouseEvent) => {
        // Convert screen coordinates to model coordinates
        const rect = container.getBoundingClientRect()
        const renderedX = mouseEvent.clientX - rect.left
        const renderedY = mouseEvent.clientY - rect.top
        const zoom = cy.zoom()
        const pan = cy.pan()
        const modelX = (renderedX - pan.x) / zoom
        const modelY = (renderedY - pan.y) / zoom

        const clickPosition = { x: modelX, y: modelY }

        // Find if we clicked on any node's expand/collapse badge
        const clickedNode = operations.findNodeWithBadgeAt(cy, clickPosition)
        if (!clickedNode) {
          return
        }

        const children = visibility.getChildren(clickedNode)
        if (children.length === 0) {
          return
        }

        // Prevent cytoscape from processing this click (no node selection)
        mouseEvent.stopPropagation()
        mouseEvent.preventDefault()

        const hasVisibleChild = visibility.collectionHasVisibleChild(children)
        const isCollapsing = hasVisibleChild

        if (isCollapsing) {
          operations.collapseNode(clickedNode)
        } else {
          operations.expandNode(clickedNode)
        }
      },
      true, // Use capture phase to intercept before cytoscape
    )
  }

  cy.on('add remove', 'node,edge', () => {
    visibility.refreshExpandCollapseState(cy)
  })
}
export namespace setupExpandCollapse {
  export interface AdditionalParams {
    defaultVisibleDepth?: number
  }
}

/**
 * Toggles between expanding all nodes and collapsing to the default depth.
 */
export function toggleExpandCollapseAll(
  cy: cytoscape.Core,
  {
    defaultVisibleDepth = DEFAULT_VISIBLE_DEPTH,
  }: toggleExpandCollapseAll.AdditionalParams = {},
): void {
  if (operations.isAllNodesExpanded(cy)) {
    operations.collapseToDepth(cy, defaultVisibleDepth)
  } else {
    operations.expandAll(cy)
  }
}
export namespace toggleExpandCollapseAll {
  export interface AdditionalParams {
    defaultVisibleDepth?: number
  }
}

/** Applies the initial visible-depth window before the first layout pass. */
function applyVisibilityByDepth(
  cy: cytoscape.Core,
  depths: Map<string, number>,
  defaultVisibleDepth: number,
): void {
  cy.nodes().forEach(node => {
    const depth = depths.get(node.id())
    visibility.setVisible(
      node,
      depth !== undefined && depth <= defaultVisibleDepth,
    )
  })
}
